import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { TaskWithFlags } from "@desk/contracts";
import type { TaskGroup } from "@/concepts/types";

/** How long a just-completed row stays (checked) before it collapses out of a list that hides it. */
export const LINGER_MS = 900;

interface Lingering {
  task: TaskWithFlags;
  groupKey: string;
  index: number;
}

/**
 * Completion feedback: when a row is checked off and the list drops it (open-only lists, Today's
 * plan), it stays in place, shown as done, then collapses before the list reflows. Only rows the
 * user completed here linger; tasks that vanish for any other reason leave immediately.
 */
export function insertLingering(groups: TaskGroup[], lingering: Lingering[]): TaskGroup[] {
  if (!lingering.length) return groups;
  const present = new Set(groups.flatMap((g) => g.tasks.map((t) => t.id)));
  const missing = lingering.filter((l) => !present.has(l.task.id));
  if (!missing.length) return groups;
  return groups.map((g) => {
    const mine = missing.filter((l) => l.groupKey === g.key).sort((a, b) => a.index - b.index);
    if (!mine.length) return g;
    const tasks = [...g.tasks];
    for (const l of mine) tasks.splice(Math.min(l.index, tasks.length), 0, l.task);
    return { ...g, tasks };
  });
}

export function useCompletionLinger(groups: TaskGroup[]) {
  const [lingering, setLingering] = useState<Lingering[]>([]);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const onCompleted = (task: TaskWithFlags) => {
    const group = groups.find((g) => g.tasks.some((t) => t.id === task.id));
    if (!group) return;
    const entry = { task: { ...task, status: "done" as const }, groupKey: group.key, index: group.tasks.findIndex((t) => t.id === task.id) };
    setLingering((ls) => [...ls.filter((l) => l.task.id !== task.id), entry]);
    const timer = setTimeout(() => {
      timers.current.delete(timer);
      setLingering((ls) => ls.filter((l) => l !== entry));
    }, LINGER_MS);
    timers.current.add(timer);
  };

  const shown = useMemo(() => insertLingering(groups, lingering), [groups, lingering]);
  const present = useMemo(() => new Set(groups.flatMap((g) => g.tasks.map((t) => t.id))), [groups]);
  /** Lingering rows that the list itself has already dropped; these get the collapse animation. */
  const leaving = (id: string) => !present.has(id) && lingering.some((l) => l.task.id === id);
  return { groups: shown, onCompleted, leaving };
}

/**
 * Rows that appear after the list first rendered (a new task, a restored one) fade in. Rows present
 * at mount, and rows that merely re-render on a refetch, don't animate.
 */
export function useNewRows(groups: TaskGroup[]) {
  const seen = useRef<Set<string> | null>(null);
  const fresh = useRef(new Set<string>());
  const ids = groups.flatMap((g) => g.tasks.map((t) => t.id));
  if (seen.current === null) seen.current = new Set(ids);
  for (const id of ids) {
    if (!seen.current.has(id)) {
      seen.current.add(id);
      fresh.current.add(id);
    }
  }
  return (id: string) => fresh.current.has(id);
}

/** Wraps a row for its motion: collapse when leaving, fade/slide in when new. */
export function RowMotion({ leaving, entering, children }: { leaving: boolean; entering: boolean; children: ReactNode }) {
  if (leaving) {
    return (
      <div className="pointer-events-none grid animate-row-out" aria-hidden>
        <div className="min-h-0 overflow-hidden">{children}</div>
      </div>
    );
  }
  if (entering) return <div className="animate-row-in">{children}</div>;
  return <>{children}</>;
}
